import { useState, useEffect, useRef } from 'react'
import { C, R, F } from '../../lib/designTokens'
import * as pk from '../../services/productKnowledgeService'

const KIND_LABEL = {
  workflow: 'Workflow', screen: 'Screen', output: 'Output', rule: 'Rule',
  exception_case: 'Exception', term: 'Term', area: 'Area', product: 'Product',
  field: 'Field', action: 'Action',
}

export default function ProductKnowledgeSearch({ onOpenScreen, onOpenWorkflow, onOpenItem, onOpenArea, onOpenProduct }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const inputRef = useRef(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  useEffect(() => {
    const q = query.trim()
    if (!q) { setResults([]); setError(null); return }
    let cancelled = false
    setLoading(true)
    const t = setTimeout(() => {
      pk.search(q)
        .then(r => { if (!cancelled) { setResults(r); setLoading(false); setError(null) } })
        .catch(e => { if (!cancelled) { setError(e); setLoading(false) } })
    }, 250) // debounce
    return () => { cancelled = true; clearTimeout(t) }
  }, [query])

  function openResult(r) {
    // catalog_search returns the unit's key and kind; each kind opens its own reader.
    // (r.title is the fallback for a search that only returns the key as the title.)
    const key = r.key || r.title
    if (r.kind === 'workflow') onOpenWorkflow(key)
    else if (r.kind === 'screen' || r.kind === 'field' || r.kind === 'action') onOpenScreen(key) // a field/action result points at its screen
    else if (r.kind === 'area') onOpenArea?.(key)
    else if (r.kind === 'product') onOpenProduct?.(key)
    else onOpenItem?.(r.kind, key)
  }

  return (
    <div style={{ maxWidth: 640 }}>
      <input
        ref={inputRef}
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder="Search Product Knowledge… / ابحث في قاعدة المعرفة…"
        style={{
          width: '100%', fontSize: F.title, padding: '12px 16px', borderRadius: R.input,
          border: `1px solid ${C.border}`, outline: 'none', fontFamily: 'inherit', background: C.surface,
        }}
      />
      <p style={{ fontSize: F.caption, color: C.sub, marginTop: 8 }}>
        Searches names and descriptions, in English and Arabic. / يبحث في الأسماء والأوصاف بالعربية والإنجليزية.
      </p>

      <div style={{ marginTop: 16 }}>
        {loading && <div style={{ color: C.sub, fontSize: F.body }}>Searching… / جارٍ البحث…</div>}
        {error && <div style={{ color: C.red, fontSize: F.body }}>Search failed. / فشل البحث.</div>}
        {!loading && !error && query.trim() && results.length === 0 && (
          <div style={{ color: C.sub, fontSize: F.body }}>No results. / لا توجد نتائج.</div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {results.map((r, i) => (
            <button key={i} onClick={() => openResult(r)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 10, textAlign: 'left',
                padding: '12px 14px', borderRadius: R.card, border: `1px solid ${C.border}`,
                background: C.surface, cursor: 'pointer', fontFamily: 'inherit',
              }}>
              <span style={{
                fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em',
                padding: '2px 7px', borderRadius: R.tag, background: C.blueBg, color: C.blue, flexShrink: 0,
              }}>
                {KIND_LABEL[r.kind] || r.kind}
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <span style={{ fontSize: F.bodyLg, color: C.ink }}>{r.title}</span>
                {r.title_ar && r.title_ar !== r.title && <span dir="rtl" style={{ fontSize: F.body, color: C.sub, textAlign: 'right' }}>{r.title_ar}</span>}
                {r.snippet && <span style={{ fontSize: F.caption, color: C.sub, marginTop: 2, lineHeight: 1.4 }}>{r.snippet}</span>}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
